import * as React from 'react'

import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceReviewProcessBasicInfoFragment,
  UpdateBasicInfoPerformanceReviewProcessDocument,
} from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CycleSelector } from './CycleSelector'
import OwnersSection from './owners/OwnersSection'

interface ReviewBasicInfoFormValues {
  id: number
  name?: string | null | undefined
  description?: string | null | undefined
}

export const BasicInfo: React.FC<{
  review: PerformanceReviewProcessBasicInfoFragment
  isConnectGoalsAndReviewsV2Enabled: boolean
}> = ({ review, isConnectGoalsAndReviewsV2Enabled }) => {
  const [updateReviewBasicInfo, { loading }] = useMutation(
    UpdateBasicInfoPerformanceReviewProcessDocument,
    {
      extractorFn: (data) => data.performanceMutations.updateBasicInfoPerformanceReviewProcess,
    }
  )

  const form = useForm<ReviewBasicInfoFormValues>({
    defaultValues: {
      id: review.id,
      name: review.name,
      description: review.description,
    },
    validate: (data) => {
      const errors: FormErrors<ReviewBasicInfoFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const response = await updateReviewBasicInfo({
        variables: {
          id: review.id,
          name: data.name,
          description: data.description,
        },
      })

      const result = handleResult(response, () => {
        // TODO: do something with form errors
      })

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return result
    },
  })

  const formHasName = !!form.values.name

  const isReviewFinished = review.status === 'finished'

  return (
    <Box gap="s32">
      <Text size="400" color="grey1000" weight="semibold">
        {i18n.t('performance_process.reviews.basic_information')}
      </Text>
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <TextField
          mandatory
          readonly={loading || isReviewFinished}
          value={form.values.name || undefined}
          label={i18n.t('performance_process.reviews.basic_information_section.title_label')}
          placeholder={i18n.t(
            'performance_process.reviews.basic_information_section.title_placeholder'
          )}
          onChange={(name) => form.setValues({ ...form.values, name: name })}
        />
        <TextArea
          readonly={loading || isReviewFinished}
          value={form.values.description || ''}
          label={i18n.t('performance_process.reviews.basic_information_section.description_label')}
          placeholder={i18n.t(
            'performance_process.reviews.basic_information_section.description_placeholder_new_review'
          )}
          onChange={(description) => form.setValues({ ...form.values, description: description })}
        />
        <Box justifyContent="flexEnd" flexDirection="row" width="full">
          <Button
            type={form.dirty ? 'primary' : 'secondary'}
            onClick={() => form.handleSubmit()}
            disabled={form.isSubmitting || !formHasName || loading || !form.dirty}
            loading={loading}
          >
            {i18n.t('common.save')}
          </Button>
        </Box>
      </Form>

      {isConnectGoalsAndReviewsV2Enabled && (
        <CycleSelector
          reviewId={review.id}
          cycleId={review.performanceCycle?.id}
          isDisabled={review.status !== 'draft'}
        />
      )}

      <OwnersSection owners={review.owners} />
    </Box>
  )
}
